import { computed } from 'vue';
import { useCourseStore } from '@/stores/courseStore';
import { usePlanStore } from '@/stores/planStore';
import { useAdaptiveRtStore } from '@/stores/adaptiveRtStore';
import { useOpenIntentStore } from '@/stores/openIntentStore';
import { getCalculationModuleById, usesCombinedSetup } from '@/config/calculationModules';
import { validateFractionationInput } from '@/composables/calculationConstants';
import { fractionationFromSelectedCourse } from '@/composables/useFractionationDraft';
import { isAdaptiveRtModule } from '@/utils/adaptiveRtModule';

function resolveStandardFractionation(planStore, courseStore) {
  const fromPlan = planStore.fractionationForSelected;
  const draft = planStore.fractionationDraft;
  const candidates = [fromPlan, draft];
  for (const f of candidates) {
    if (validateFractionationInput(f).ok) return f;
  }
  const fromCourse = fractionationFromSelectedCourse(courseStore.selectedCourse);
  if (validateFractionationInput(fromCourse).ok) return fromCourse;
  return draft || fromPlan || fromCourse || null;
}

/**
 * Куда вести пользователя из полосы контекста: расчёт или шаг настройки (параметры / ГДО).
 */
export function useCalculationStepNavigation() {
  const courseStore = useCourseStore();
  const planStore = usePlanStore();
  const adaptiveRtStore = useAdaptiveRtStore();
  const openIntentStore = useOpenIntentStore();

  const canShowStepLink = computed(
    () => !!courseStore.selectedCourseId && !!courseStore.activeCalculationModuleId
  );

  const stepNavigation = computed(() => {
    const intentModeId = openIntentStore.intent?.calculationModuleId || null;
    const modeId = intentModeId || courseStore.activeCalculationModuleId;
    if (!modeId) {
      return {
        to: { path: '/app/calculate' },
        label: 'Выбрать модуль',
      };
    }
    const mode = getCalculationModuleById(modeId);
    if (!mode) {
      return {
        to: { path: '/app/calculate' },
        label: 'Выбрать модуль',
      };
    }
    const modeQuery = { mode: mode.id };

    if (isAdaptiveRtModule(modeId)) {
      if (adaptiveRtStore.canRunWizardValidate) {
        return {
          to: { path: mode.route, query: modeQuery },
          label: 'Перейти к расчёту',
        };
      }
      return {
        to: { path: '/app/calculate/adaptive-rt/upload', query: modeQuery },
        label: 'Перейти к загрузке ГДО',
      };
    }

    const fractionation = resolveStandardFractionation(planStore, courseStore);
    const fractionationReady = validateFractionationInput(fractionation).ok;
    const planId = planStore.selectedPlanId;

    if (fractionationReady && planId) {
      return {
        to: {
          path: mode.route,
          query: { ...modeQuery, plan_id: planId },
        },
        label: 'Перейти к расчёту',
      };
    }

    if (!fractionationReady && !usesCombinedSetup(modeId)) {
      return {
        to: { path: '/app/calculate/params', query: modeQuery },
        label: 'Перейти к параметрам d, nf',
      };
    }

    return {
      to: { path: '/app/calculate/upload', query: modeQuery },
      label: 'Перейти к загрузке ГДО',
    };
  });

  return {
    canShowStepLink,
    stepNavigation,
  };
}
